import { GetStaticProps } from 'next';

import {
	BodyTextFieldsFragment,
	PrivacyPolicyFieldsFragment,
	PageInformationFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding
} from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetPrivacyPolicy } from 'api/graphql-helper';

import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { BaseModule } from 'modules/base-module/base-module';
import IntroHeader from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';

interface IPrivacyPolicyProps extends IBasePageProps {
	privacyPolicy?: PrivacyPolicyFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
}

const PrivacyPolicy = (props: IPrivacyPolicyProps): React.JSX.Element => {
	const seoModule = props?.privacyPolicy?.seo;

	const headerDefaultOptions = {
		backgroundColor: props.privacyPolicy?.header
			?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.privacyPolicy?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader module={props.privacyPolicy?.header} slug={ROUTES.PRIVACY_POLICY.fullPath} />
			</BaseModule>
			<BaseModule>
				<BodyText module={props.privacyPolicy?.bodytext as BodyTextFieldsFragment} small />
			</BaseModule>
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetPrivacyPolicy(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, privacyPolicy } = result.props.data;

		if (privacyPolicy == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				privacyPolicy: privacyPolicy.data?.attributes,
				id: privacyPolicy.data?.documentId,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'privacy-policy',
						contentTypeId: privacyPolicy?.data?.documentId
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in privacy policy pagina', error);

		return {
			notFound: true
		};
	}
};

export default PrivacyPolicy;
